import React, { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { Search } from '@material-ui/icons'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import { INSIGHTS_API_BASE_URL } from '../../constants'
import { FormattedDateTime } from '../formatted-date-time'
import { HtmlReportListResponse, HtmlReportMeta } from './types'
import { insightsReportUrl, matchesHtmlReport } from './shareUrl'

const useStyles = makeStyles(theme => ({
  headCell: {
    fontWeight: 600,
    background: '#eee'
  },
  row: {
    cursor: 'pointer'
  },
  searchField: {
    width: 320,
    background: '#fff'
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  labelLink: {
    color: 'inherit'
  }
}))

type SortKey = 'createdAt' | 'label' | 'updatedAt'
type SortDir = 'asc' | 'desc'

export const InternalReportsList: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const { getAccessTokenSilently } = useAuth0()
  const [reports, setReports] = useState<HtmlReportMeta[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('createdAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const base = INSIGHTS_API_BASE_URL?.replace(/\/$/, '')
      if (!base) {
        setError('Insights URL is not configured')
        setLoading(false)
        return
      }
      try {
        const token = await getAccessTokenSilently()
        const response = await fetch(`${base}/html-reports`, {
          headers: { 'X-Vestaboard-Token': token }
        })
        const body = (await response.json()) as HtmlReportListResponse
        if (cancelled) return
        if (!response.ok || !body.success) {
          setError(!body.success ? body.error : `Failed to load reports (${response.status})`)
          setReports([])
          return
        }
        setReports(body.reports)
        setError(null)
      } catch {
        if (!cancelled) {
          setError('Failed to load reports')
          setReports([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently])

  const visible = useMemo(() => {
    const filtered = reports.filter(report => matchesHtmlReport(report, search))
    const sorted = [...filtered].sort((a, b) => {
      const left = a[sortKey]
      const right = b[sortKey]
      const compared = left.localeCompare(right)
      return sortDir === 'asc' ? compared : -compared
    })
    return sorted
  }, [reports, search, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'))
      return
    }
    setSortKey(key)
    setSortDir(key === 'label' ? 'asc' : 'desc')
  }

  const copyShareLink = async (event: React.MouseEvent, slug: string) => {
    event.stopPropagation()
    const url = insightsReportUrl(slug)
    if (!url) return
    await navigator.clipboard.writeText(url)
    setCopiedSlug(slug)
  }

  if (loading) return <CircularProgress />

  return (
    <>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Internal reports</Typography>
        <TextField
          className={classes.searchField}
          placeholder='Search label, description, tags'
          value={search}
          onChange={event => setSearch(event.target.value)}
          variant='outlined'
          size='small'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search fontSize='small' />
              </InputAdornment>
            )
          }}
        />
      </FlexHorizontal>
      <Spacer size='large' />
      {error && (
        <>
          <Alert severity='error'>{error}</Alert>
          <Spacer size='large' />
        </>
      )}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell} sortDirection={sortKey === 'label' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'label'}
                    direction={sortKey === 'label' ? sortDir : 'asc'}
                    onClick={() => toggleSort('label')}>
                    Label
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headCell}>Tags</TableCell>
                <TableCell className={classes.headCell} sortDirection={sortKey === 'createdAt' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'createdAt'}
                    direction={sortKey === 'createdAt' ? sortDir : 'desc'}
                    onClick={() => toggleSort('createdAt')}>
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headCell} sortDirection={sortKey === 'updatedAt' ? sortDir : false}>
                  <TableSortLabel
                    active={sortKey === 'updatedAt'}
                    direction={sortKey === 'updatedAt' ? sortDir : 'desc'}
                    onClick={() => toggleSort('updatedAt')}>
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.headCell}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>No reports match the current search.</TableCell>
                </TableRow>
              ) : (
                visible.map(report => (
                  <TableRow
                    hover
                    className={classes.row}
                    key={report.slug}
                    onClick={event => {
                      const path = `/internal-reports/${report.slug}`
                      if (event.metaKey || event.ctrlKey) {
                        window.open(path, '_blank', 'noopener,noreferrer')
                        return
                      }
                      history.push(path)
                    }}>
                    <TableCell>
                      <Link
                        className={classes.labelLink}
                        component={RouterLink}
                        to={`/internal-reports/${report.slug}`}
                        onClick={event => event.stopPropagation()}>
                        <Typography variant='subtitle1'>{report.label}</Typography>
                      </Link>
                      <Typography variant='body2' color='textSecondary'>
                        {report.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {report.tags.map(tag => (
                        <Chip
                          key={tag}
                          className={classes.chip}
                          size='small'
                          label={tag}
                          onClick={event => {
                            event.stopPropagation()
                            setSearch(tag)
                          }}
                        />
                      ))}
                    </TableCell>
                    <TableCell>
                      <FormattedDateTime value={Date.parse(report.createdAt)} />
                    </TableCell>
                    <TableCell>
                      <FormattedDateTime value={Date.parse(report.updatedAt)} />
                    </TableCell>
                    <TableCell>
                      <Button
                        size='small'
                        variant='outlined'
                        onClick={event => copyShareLink(event, report.slug)}>
                        {copiedSlug === report.slug ? 'Copied' : 'Copy Insights link'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Box mt={2}>
        <Typography variant='body2' color='textSecondary'>
          Slack unfurls the Insights URL. Opening it in a browser sends you here.
        </Typography>
      </Box>
    </>
  )
}
